'use client';

import type { CSSProperties, RefObject } from 'react';
import { composeRenderProps } from 'react-aria-components';

import { isTouchDevice } from '@infinex/ui-utils/dom';

/**
 * Scales the button when pressed, on touch devices only.
 *
 * @see {@link pressScale}
 */
export const pressScaleTouch: typeof pressScale = (ref, style) => {
  if (isTouchDevice()) return pressScale(ref, style);
  return typeof style === 'function' ? style : () => style as CSSProperties;
};

/**
 * Scales the button when pressed, using the element’s height and width to
 * determine perspective.
 */
export function pressScale<R extends { isPressed: boolean }>(
  ref: RefObject<HTMLElement | null>,
  style?: CSSProperties | ((renderProps: R) => CSSProperties)
): (renderProps: R) => CSSProperties {
  return composeRenderProps(style, (style, renderProps: R) => {
    const el = ref.current;
    const willChange = `${style?.willChange ?? ''} transform`;

    if (renderProps.isPressed && el) {
      // Map perspective to button width so that press scale (tz) is consistent.
      // Otherwise, the perceived scale effect increases linearly with the width
      // of the button, creating an uncomfortable experience.
      const { width = 0, height = 0 } = el.getBoundingClientRect() ?? {};
      const perspective = Math.ceil(Math.max(height, width / 3));
      return {
        ...style,
        willChange,
        opacity: style?.opacity ?? 0.9, // exaggerate depressed appearance
        transitionDuration: '75ms', // press should feel quick
        transform: isTouchDevice()
          ? `${style?.transform ?? ''} perspective(${perspective}px) translate3d(0, 0, -2px)`
          : `${style?.transform ?? ''} translate3d(0, 1px, 0)`,
      };
    }

    return { ...style, willChange };
  });
}
